import LiveChannelOnlineRequestService from '../../components/widgets/live-channel-online/service-live-channel-online';
import LiveStreamingAllRequestService from '../../components/widgets/live-channel-online/live-streaming-all-request.service';
import LiveStreamEventModel from '../../models/livestream/livestream-event.model';
import LiveStreamMappedEventModel from '../../models/livestream/livestream-mapped-event.model';

/**
 * Group events by sport
 * @param {{groupedSports: {[sid:string]:LiveStreamEventModel & {tournaments: {[tid:string]:string}}}}} acc - acumulator
 * @param {LiveStreamEventModel} match - match to add to sport
 */
function groupBySport(acc, match) {
  if (!acc.groupedSports[match.externalSportId]) {
    acc.groupedSports[match.externalSportId] = {
      id: match.externalSportId,
      name: match.sportName,
      class: match.sportClass,
      tournaments: {},
    };
  }
  if (!match.endDate) {
    acc.groupedSports[match.externalSportId].tournaments[match.externalTournamentId] =
      `${match.categoryName}${match.categoryName ? " - " : ""}${match.tournamentName}`;
  }
}

/**
 * Group events by tournaments
 * @param {{groupedTournaments: {[tid:string]:LiveStreamEventModel[]}}} acc - acumulator
 * @param {LiveStreamEventModel} match - match to add to tournament
 */
function groupByTournament(acc, match) {
  if (!acc.groupedTournaments[match.externalTournamentId]) {
    acc.groupedTournaments[match.externalTournamentId] = [];
  }
  acc.groupedTournaments[match.externalTournamentId].push(match);
}

const getDefaultState = ({
  pId = 1,
  ie = {},
  ee = {},
  il = false
}) => ({
  groupedSports: {},
  groupedTournaments: {},
  integrationBrokerId: {},
  selectedSport: '',
  selectedProvider: pId,
  /**
   * This object is generated to allow
   * searching in O(1) time complexity
   */
  internalMatches: ie,
  externalMatches: ee,
  initalLoad: il,
});

/**
 * Vuex store - LiveStreamingModule module
 */
export const LiveStreamingModule = {
  state: getDefaultState({}),
  mutations: {
    changeStreamProvider(state, providerId) {
      state.selectedProvider = providerId;
    },
    changeStreamSport(state, spordId) {
      state.selectedSport = spordId;
    },
    setStreamingMatches(state, data) {
      const { groupedSports, groupedTournaments } = data;
      // Set selected sport to be first sport
      state.selectedSport = Object.keys(groupedSports)[0] || '';

      state.groupedSports = groupedSports;
      state.groupedTournaments = groupedTournaments;
    },
    setAllLiveStreamingMatches(state, payload) {
      const { externalMatches, internalMatches, integrationBrokerId } = payload.reduce((acc, lsm) => {
        const mappedMatch = new LiveStreamMappedEventModel(lsm);
        acc.externalMatches[mappedMatch.externalEventId] = mappedMatch;
        acc.integrationBrokerId[mappedMatch.integrationBrokerId] = mappedMatch.integrationBrokerId;
        if (mappedMatch.internalEventId) {
          acc.internalMatches[mappedMatch.internalEventId] = mappedMatch.externalEventId;
        }
        return acc;
      }, {
        integrationBrokerId: {},
        externalMatches: {},
        internalMatches: {},
      });
      state.integrationBrokerId = integrationBrokerId;
      state.externalMatches = externalMatches;
      state.internalMatches = internalMatches;
      state.initalLoad = true;
    },
    resetProviderStreamingState(state) {
      Object.assign(state, getDefaultState({
        pId: state.selectedProvider,
        ie: state.internalMatches,
        ee: state.externalMatches,
        il: state.initalLoad,
      }));
    },
  },
  actions: {
    getAllLiveStreamingMatches({ commit }) {
      const liveStreamingListRequest = new LiveStreamingAllRequestService();
      liveStreamingListRequest.execute((resp) => {
        const streamList = (resp.data && resp.data.slt) || [];
        commit('setAllLiveStreamingMatches', streamList);
      }, () => {
        commit('setAllLiveStreamingMatches', []);
      });
    },
    getProviderStreamingMatches({ state, commit, dispatch }) {
      const liveChannelOnlineRequestService = new LiveChannelOnlineRequestService(state.selectedProvider);
      liveChannelOnlineRequestService.execute((resp) => {
        if (!resp.data || !resp.data.slt) {
          commit('resetProviderStreamingState');
          return;
        }
        const data = resp.data.slt.reduce((acc, m) => {
          const match = new LiveStreamEventModel(m);
          if ((match.externalSportId === 'HR' || match.externalSportId === 'DG')) {
            let tournamentName = match.tournamentName.toLowerCase().replace(" ", "-");
            match.externalTournamentId = tournamentName;
          }
          groupBySport(acc, match);
          groupByTournament(acc, match);
          if (state.initalLoad && !state.externalMatches[match.externalEventId]) {
            acc.newMatch = true;
          }
          return acc;
        }, {
          groupedSports: {},
          groupedTournaments: {},
          newMatch: false,
        });
        // New matches found refresh the event map list
        if (data.newMatch) {
          dispatch('getAllLiveStreamingMatches');
        }
        commit('setStreamingMatches', data);
      }, () => { commit('resetProviderStreamingState'); });
    },
    changeStreamProvider({ commit, dispatch }, providerId) {
      commit('changeStreamProvider', providerId);
      dispatch('getProviderStreamingMatches');
    },
  },
  getters: {
    getExternalLiveStreamingMatch(state) {
      return eeid => state.externalMatches[eeid] || {};
    },
    getInternalLiveStreamingMatch(state, getters) {
      return ieid => getters.getExternalLiveStreamingMatch(state.internalMatches[ieid] || '');
    },
  }
};
